import React, { useState } from 'react';
import Manipulative from './Manipulative';
import Simulation from './Simulation';
import Solver from './Solver';
import LumiTutor from './LumiTutor';
import LeChatelier from './hsc/LeChatelier';
import PunnettSquare from './hsc/PunnettSquare';
import MonetaryPolicy from './hsc/MonetaryPolicy';
import ProjectileHSC from './hsc/ProjectileHSC';
import { TEST_INTERACTIVES } from './TestInteractives';

// HSC bespoke components — mapped by subtype.
const HSC_COMPONENTS = {
  le_chatelier:     LeChatelier,
  punnett:          PunnettSquare,
  monetary_policy:  MonetaryPolicy,
  projectile_hsc:   ProjectileHSC,
};

const T = {
  bg: '#0D1117',
  surface: '#161B22',
  border: '#30363D',
  text: '#E6EDF3',
  textMid: '#8B949E',
  accent: '#0B8A7E',
  correct: '#10B981',
};

function InteractiveFrame({ interactive, lumi }) {
  const [status, setStatus] = useState('idle');
  const [attempts, setAttempts] = useState(0);
  const onComplete = () => setStatus('completed');
  const onGiveUp = () => setStatus('gave_up');
  const onInteraction = () => setAttempts(a => a + 1);
  const interactiveId = interactive.interactive_id;

  let body = null;
  if (interactive.type === 'manipulative') {
    body = (
      <Manipulative
        type={interactive.subtype}
        config={interactive.config}
        prompt={interactive.prompt}
        successCondition={interactive.success_condition}
        onCorrect={interactive.on_correct}
        onWrongHints={interactive.on_wrong_hints}
        feedbackOnStuck={interactive.feedback_on_stuck}
        interactiveId={interactiveId}
        onInteraction={onInteraction}
        onComplete={onComplete}
        onGiveUp={onGiveUp}
      />
    );
  } else if (interactive.type === 'simulation') {
    body = (
      <Simulation
        type={interactive.subtype}
        parameters={interactive.parameters}
        observables={interactive.observables}
        prompt={interactive.prompt}
        successCondition={interactive.success_condition}
        onCorrect={interactive.feedback_on_correct}
        onWrongHints={interactive.on_wrong_hints}
        feedbackOnStuck={interactive.feedback_on_stuck}
        interactiveId={interactiveId}
        onInteraction={onInteraction}
        onComplete={onComplete}
        onGiveUp={onGiveUp}
      />
    );
  } else if (interactive.type === 'solver') {
    body = (
      <Solver
        problem={interactive.problem}
        solutionSteps={interactive.solution_steps}
        mode={interactive.mode}
        prompt={interactive.prompt}
        interactiveId={interactiveId}
        onComplete={onComplete}
        onGiveUp={onGiveUp}
      />
    );
  } else if (interactive.type === 'hsc') {
    const Comp = HSC_COMPONENTS[interactive.subtype];
    if (Comp) body = <Comp interactiveId={interactiveId} />;
  }

  const statusColor = status === 'completed' ? T.correct : status === 'gave_up' ? '#EF4444' : T.textMid;
  const statusLabel = status === 'completed' ? '✓ completed' : status === 'gave_up' ? '✗ gave up' : `${attempts} interactions`;

  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 12, color: T.textMid, textTransform: 'uppercase', letterSpacing: 0.7, fontWeight: 700 }}>
            {interactive.type} · {interactive.subtype || interactive.mode}
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: T.text, marginTop: 2 }}>{interactive.title}</div>
        </div>
        <div style={{ fontSize: 12, color: statusColor, fontWeight: 700 }}>{statusLabel}</div>
      </div>
      {body}
      {interactive.learning_objective && (
        <div style={{ marginTop: 8, fontSize: 12, color: T.textMid, fontStyle: 'italic' }}>
          Learning objective: {interactive.learning_objective}
        </div>
      )}
      {/* Inline Lumi for the specific card the user selected as "live tutor" */}
      {lumi && (
        <div style={{ marginTop: 12 }}>
          <LumiTutor
            interactiveId={interactiveId}
            interactiveTitle={interactive.title}
            currentProblem={interactive.problem || interactive.prompt}
            correctSolution={interactive.correctSolution}
            yearLevel={interactive.yearLevel || 'Year 12'}
            curriculum={interactive.curriculum || 'NSW HSC'}
            placement="inline"
          />
        </div>
      )}
    </div>
  );
}

export default function PreviewPage() {
  // Pick which interactive to mount Lumi against via ?lumi=<id>.
  // Default: the integration solver (TEST 1 from the spec).
  const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const lumiFor = params.get('lumi') || 'integration_solver_p1';

  return (
    <div style={{
      minHeight: '100vh', background: T.bg, color: T.text,
      padding: '32px 20px', fontFamily: "'Plus Jakarta Sans', sans-serif",
    }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 11, color: T.textMid, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>
            Interactive primitives · Checkpoint 1 · Lumi tutor layer
          </div>
          <h1 style={{ fontSize: 26, fontWeight: 900, margin: '6px 0 4px' }}>
            Empirio Interactive Preview
          </h1>
          <p style={{ color: T.textMid, fontSize: 14, margin: 0 }}>
            Four hand-authored samples — primitives + the Lumi in-interactive tutor on the integration solver.
          </p>
        </div>
        {TEST_INTERACTIVES.map(i => (
          <InteractiveFrame
            key={i.interactive_id}
            interactive={i}
            lumi={i.interactive_id === lumiFor}
          />
        ))}
      </div>
    </div>
  );
}
